import { Item, roundMinutes, isDayPrunable } from "../model/item";
import { entityJsonToItem } from "../services/convert";
import {
    Action,
    ItemRenameAction,
    ItemDurationChangeAction,
    ItemLockChangeAction,
    ItemOrderChangeAction,
    ItemCreateAfterAction,
    ItemCreateBeforeAction,
    ItemRemoveAction,
    UiDayChangeAction,
    ITEM_RENAME,
    ITEM_CREATE_AFTER,
    ITEM_CREATE_BEFORE,
    ITEM_DURATION_CHANGE,
    ITEM_LOCK_CHANGE,
    ITEM_ORDER_CHANGE,
    ITEM_REMOVE,
    UNDO,
    REDO,
    UI_DAY_CHANGE,
    API_ITEMS_GET_DONE,
    API_ITEMS_PATCH_DONE,
} from "../actions";
import { isEmpty, insertAfterIndex, insertBeforeIndex } from "../utils/array";
import undoable, { includeAction, StateWithHistory } from "redux-undo";
import { State } from ".";
import { getCurrentDay } from "./currentDay.selectors";
import { generateId } from "../model/id";
import { getTodayDate, getMinutesFromDate } from "../utils/date";

export type ItemsStateWithHistory = {
    [date: string]: StateWithHistory<Array<Item>>;
};

const getInitialItems = (date: string): Array<Item> => {
    return [
        {
            id: generateId(),
            title: ``,
            duration: 30,
            lock:
                date === getTodayDate()
                    ? roundMinutes(getMinutesFromDate(new Date(), date))
                    : 9 * 60,
        },
    ];
};

const ITEM_INIT = `ITEM_INIT`;

type ItemInitAction = {
    type: typeof ITEM_INIT;
    payload: { date: string };
};

const itemInit = (date: string): ItemInitAction => ({
    type: ITEM_INIT,
    payload: { date },
});

type ItemsActions =
    | ItemInitAction
    | ItemRenameAction
    | ItemDurationChangeAction
    | ItemLockChangeAction
    | ItemOrderChangeAction
    | ItemCreateAfterAction
    | ItemCreateBeforeAction
    | ItemRemoveAction
    | UiDayChangeAction;

const itemsReducer = (
    state: Array<Item> = [],
    action: ItemsActions,
): Array<Item> => {
    switch (action.type) {
        case ITEM_INIT: {
            if (state.length === 0) {
                return getInitialItems(action.payload.date);
            }
            return state;
        }
        case ITEM_RENAME: {
            const { index, item } = action.payload;
            if (isEmpty(state)) {
                return [item];
            }
            return state.map((v, i) => (i === index ? item : v));
        }
        case ITEM_CREATE_AFTER: {
            const { index, item } = action.payload;
            return insertAfterIndex(item, index, state || []);
        }
        case ITEM_CREATE_BEFORE: {
            const { index, item } = action.payload;
            const newItem =
                index === 0
                    ? {
                          ...item,
                          lock: state[0].lock,
                      }
                    : item;
            if (!isEmpty(state) && index === 0) {
                state = state.map((item, i) =>
                    i === 0 ? { ...item, lock: null } : item,
                );
            }
            return insertBeforeIndex(newItem, index, state || []);
        }
        case ITEM_DURATION_CHANGE: {
            const { index, item } = action.payload;
            if (item.duration < 0) {
                return state;
            }
            return state.map((oldItem, i) => (i === index ? item : oldItem));
        }
        case ITEM_LOCK_CHANGE: {
            const { index, item } = action.payload;
            if (index === 0 && item.lock == null) {
                throw new Error(
                    `Can't unlock first item: first item should always be locked`,
                );
            }
            return state.map((oldItem, i) => (i === index ? item : oldItem));
        }
        case ITEM_ORDER_CHANGE: {
            const { from, to } = action.payload;
            if (from === to) {
                return state;
            }
            if (state === null || state.length < 2) {
                return state;
            }
            if (from === 0) {
                const { lock } = state[0];
                state = state.map((item, i) =>
                    i === 0
                        ? { ...item, lock: null }
                        : i === 1
                        ? { ...item, lock: item.lock || lock }
                        : item,
                );
            }
            if (to === 0) {
                const { lock } = state[0];
                state = state.map((item, i) =>
                    i === 0
                        ? { ...item, lock: null }
                        : i === from
                        ? { ...item, lock: item.lock || lock }
                        : item,
                );
            }
            if (from < to) {
                return state.map((item, i) =>
                    i < from || to < i
                        ? item
                        : i === to
                        ? state[from]
                        : state[i + 1],
                );
            }
            if (to < from) {
                return state.map((item, i) =>
                    i < to || from < i
                        ? item
                        : i === to
                        ? state[from]
                        : state[i - 1],
                );
            }
            throw new Error(`Invalid action`);
        }
        case ITEM_REMOVE: {
            const { index, date } = action.payload;
            if (isEmpty(state)) {
                return state;
            }
            const { lock } = state[0];
            if (index === 0) {
                state = state.map((item, i) =>
                    i === 1 ? { ...item, lock } : item,
                );
            }
            if (state.length === 1) {
                return getInitialItems(date);
            }
            return state.filter((item, i) => i !== index);
        }
        default:
            return state;
    }
};

const undoableConfig = {
    limit: 10,
    groupBy: (action: ItemsActions) => {
        switch (action.type) {
            case ITEM_DURATION_CHANGE:
            case ITEM_LOCK_CHANGE:
            case ITEM_RENAME:
                return `${action.type}_${action.payload.item.id}`;
            default:
                return null;
        }
    },
    ignoreInitialState: true,
    filter: includeAction([
        ITEM_CREATE_AFTER,
        ITEM_CREATE_BEFORE,
        ITEM_DURATION_CHANGE,
        ITEM_LOCK_CHANGE,
        ITEM_ORDER_CHANGE,
        ITEM_REMOVE,
        ITEM_RENAME,
    ]),
    undoType: UNDO,
    redoType: REDO,
};

const omit = <T extends Record<string, any>>(
    object: T,
    keyToRemove: string,
): T => {
    return Object.keys(object).reduce((result, key) => {
        if (key !== keyToRemove) {
            result[key] = object[key];
        }
        return result;
    }, {} as any);
};

export const daysReducer = (
    state: ItemsStateWithHistory = {},
    action: Action,
    entireState: State,
): ItemsStateWithHistory => {
    switch (action.type) {
        case UI_DAY_CHANGE: {
            const currentDay = getCurrentDay(entireState);
            const newDay = action.payload.date;
            const dayData = state[currentDay] && state[currentDay].present;
            let newState = state;
            if (currentDay !== newDay && isDayPrunable(dayData)) {
                newState = omit(newState, currentDay);
            }
            const newDayData = newState[newDay] && newState[newDay].present;
            if (isEmpty(newDayData)) {
                const itemsReducerWithHistory = undoable(
                    itemsReducer,
                    undoableConfig,
                );
                const newDayDataInitialized = itemsReducerWithHistory(
                    undefined,
                    { type: `@@INIT` },
                );
                newState = {
                    ...newState,
                    [newDay]: itemsReducerWithHistory(
                        newDayDataInitialized,
                        itemInit(newDay),
                    ),
                };
            }
            return newState;
        }
        case ITEM_CREATE_AFTER:
        case ITEM_CREATE_BEFORE:
        case ITEM_DURATION_CHANGE:
        case ITEM_LOCK_CHANGE:
        case ITEM_ORDER_CHANGE:
        case ITEM_REMOVE:
        case ITEM_RENAME:
        case UNDO:
        case REDO: {
            const { date } = action.payload;
            const itemsReducerWithHistory = undoable(
                itemsReducer,
                undoableConfig,
            );
            const dayData =
                state[date] ||
                itemsReducerWithHistory(undefined, { type: `@@INIT` });
            return {
                ...state,
                [date]: itemsReducerWithHistory(dayData, action),
            };
        }
        case API_ITEMS_GET_DONE:
        case API_ITEMS_PATCH_DONE: {
            const incoming = action.payload;
            // entireState is pre-action: dates still dirty here were edited
            // while the request was in flight (a POST response excludes the
            // pushed dates by server contract) — local pending edits win
            const dirtyDates = entireState.data.sync.dirtyDates;
            const currentDay = getCurrentDay(entireState);
            return Object.keys(incoming).reduce((result, date) => {
                if (dirtyDates[date] != null) {
                    return result;
                }
                const rawItems = incoming[date];
                if (rawItems.length === 0) {
                    // deletion tombstone; the focused day must keep existing,
                    // recreated fresh and NOT marked dirty (the deletion is
                    // not pushed back — same as mobile)
                    if (date === currentDay) {
                        return {
                            ...result,
                            [date]: {
                                past: [],
                                present: getInitialItems(date),
                                future: [],
                            },
                        };
                    }
                    return omit(result, date);
                }
                return {
                    ...result,
                    [date]: {
                        past: [],
                        present: rawItems.map((raw) =>
                            entityJsonToItem(raw, date),
                        ),
                        future: [],
                    },
                };
            }, state);
        }
        default:
            return state;
    }
};
